import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useTranslation } from "react-i18next";

import "./styles/tailwind.css";

import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";

import QuestionForm from "./pages/user/QuestionForm";
import QrGenerator from "./pages/user/QrGenerator";
import StaticPage from "./pages/user/StaticPage";
import HelpPage from "./pages/user/HelpPage";
import RepportPage from "./pages/user/ReportPage";
import UserRegister from "./pages/user/UserRegister";

import CreateNewPassword from "./pages/auth/CreateNewPassword";
import ForgetPassword from "./pages/auth/ForgetPassword";
import Login from "./pages/auth/Login";
import PasswordSuccess from "./pages/auth/PasswordSuccess";
import Signup from "./pages/auth/Signup";
import VerifyEmail from "./pages/auth/VerifyEmail";

import AdminDashboard from "./pages/admin/AdminDashboard";
import UserRegisterWeb from "./pages/admin/UserRegisterWeb";

import DownloadApp from "./pages/marketing/DownloadApp";
/* Pages landing seulement */
import AssistByScanHomePage from "./pages/marketing/AssistByScanHomePage";
import FeaturesPage from "./pages/marketing/FeaturesPage";
import HowItWorksPage from "./pages/marketing/HowItWorksPage";
import ContactPage from "./pages/marketing/ContactPage";
import PricingPage from "./pages/marketing/PricingPage";

const clearStorageKeepingTheme = () => {
  const theme = localStorage.getItem("theme");
  localStorage.clear();
  if (theme) localStorage.setItem("theme", theme);
};

const getInitialTheme = () => {
  const storedTheme = localStorage.getItem("theme");
  if (storedTheme === "dark" || storedTheme === "light") return storedTheme;

  if (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)").matches
  ) {
    return "dark";
  }

  return "light";
};

function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}

function App() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const rtlLangs = ["ar", "fa"];
    const currentLang = (i18n.language || "fr").split("-")[0];
    const isRtl = rtlLangs.includes(currentLang);

    document.documentElement.lang = currentLang;
    document.documentElement.dir = isRtl ? "rtl" : "ltr";
  }, [i18n.language]);

  const getInitialAuth = () => {
    const expiry = localStorage.getItem("authExpiry");
    const isAuth = localStorage.getItem("isAuthenticated") === "true";
    const now = Date.now();

    if (isAuth && expiry && now < parseInt(expiry, 10)) return true;

    clearStorageKeepingTheme();
    return false;
  };

  const [isAuthenticated, setIsAuthenticated] = useState(getInitialAuth);
  const [userEmail, setUserEmail] = useState(() => localStorage.getItem("userEmail"));
  const [userRole, setUserRole] = useState(() => localStorage.getItem("userRole"));
  const [userApplication, setUserApplication] = useState(() =>
    localStorage.getItem("userApplication")
  );
  const [theme, setTheme] = useState(getInitialTheme);
  const [otpEmail, setOtpEmail] = useState(null);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    if (
      isAuthenticated &&
      ["/login", "/signup", "/verify"].includes(location.pathname)
    ) {
      if (userRole?.toLowerCase() === "admin") {
        navigate("/admin-dashboard", { replace: true });
      } else {
        navigate("/qr-generator", { replace: true });
      }
    }
  }, [isAuthenticated, userRole, location.pathname, navigate]);

  useEffect(() => {
    const checkAuthExpiry = () => {
      const expiry = localStorage.getItem("authExpiry");

      if (expiry && Date.now() >= parseInt(expiry, 10)) {
        clearStorageKeepingTheme();
        setIsAuthenticated(false);
        setUserEmail(null);
        setUserRole(null);
        setUserApplication(null);
        navigate("/login", { replace: true });
      }
    };

    const intervalId = setInterval(checkAuthExpiry, 60 * 1000);
    checkAuthExpiry();

    return () => clearInterval(intervalId);
  }, [navigate]);

  const handleLogin = (email, role, application) => {
    setIsAuthenticated(true);
    setUserEmail(email);
    setUserRole(role);
    setUserApplication(application);

    const expiryTime = Date.now() + 10 * 60 * 1000;

    localStorage.setItem("isAuthenticated", "true");
    localStorage.setItem("userEmail", email);
    localStorage.setItem("userRole", role || "");
    localStorage.setItem("userApplication", application || "");
    localStorage.setItem("authExpiry", expiryTime.toString());

    if (role?.toLowerCase() === "admin") {
      navigate("/admin-dashboard");
    } else {
      navigate("/qr-generator");
    }
  };

  const handleLogout = () => {
    clearStorageKeepingTheme();
    setIsAuthenticated(false);
    setUserEmail(null);
    setUserRole(null);
    setUserApplication(null);
    navigate("/login", { replace: true });
  };

  const toggleTheme = () => {
    setTheme((current) => (current === "dark" ? "light" : "dark"));
  };

  const PrivateRoute = ({ children }) => {
    return isAuthenticated ? children : <Navigate to="/login" replace />;
  };

  const authReady = !isAuthenticated || (userEmail && userRole && userApplication);
  if (!authReady) return null;

  /* Toutes les pages qui doivent avoir la navbar landing */
  const landingPaths = [
    "/",
    "/features",
    "/how-it-works",
    "/contact",
    "/pricing",
  ];

  const isLandingPage = landingPaths.includes(location.pathname);

  return (
    <div className="app-shell">
      <div className="assist">
        <Navbar
          isLanding={isLandingPage}
          isAuthenticated={isAuthenticated}
          onLogout={handleLogout}
          userRole={userRole}
          userApplication={userApplication}
          theme={theme}
          onToggleTheme={toggleTheme}
        />
      </div>

      <main className={isLandingPage ? "landing-main" : "app-main"}>
        <Routes>
          {/* Landing pages */}
          <Route path="/" element={<AssistByScanHomePage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/pricing" element={<PricingPage />} />

          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/qr-generator" replace />
              ) : (
                <Login
                  setIsAuthenticated={(auth, email, role, application) => {
                    if (auth) handleLogin(email, role, application);
                  }}
                />
              )
            }
          />

          <Route
            path="/signup"
            element={
              isAuthenticated ? (
                <Navigate to="/qr-generator" replace />
              ) : otpEmail ? (
                <Navigate to="/verify" replace />
              ) : (
                <Signup setOtpEmail={setOtpEmail} />
              )
            }
          />

          <Route path="/download" element={<DownloadApp />} />
          <Route path="/verify" element={<VerifyEmail />} />
          <Route path="/password-success" element={<PasswordSuccess />} />

          <Route
            path="/forgot-password"
            element={<ForgetPassword setOtpEmail={setOtpEmail} />}
          />

          <Route
            path="/create-new-password"
            element={<CreateNewPassword setOtpEmail={setOtpEmail} />}
          />

          <Route
            path="/admin-dashboard"
            element={
              isAuthenticated && userRole?.toLowerCase() === "admin" ? (
                <AdminDashboard userEmail={userEmail} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          <Route
            path="/qr-generator"
            element={
              <PrivateRoute>
                <QrGenerator
                  userEmail={userEmail}
                  userRole={userRole}
                  userApplication={userApplication}
                />
              </PrivateRoute>
            }
          />

          <Route
            path="/questions"
            element={
              <PrivateRoute>
                <QuestionForm />
              </PrivateRoute>
            }
          />

          <Route
            path="/static_page"
            element={
              <PrivateRoute>
                <StaticPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/help_page"
            element={
              <PrivateRoute>
                <HelpPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/report"
            element={
              <PrivateRoute>
                <RepportPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/user_register"
            element={
              <PrivateRoute>
                <UserRegister />
              </PrivateRoute>
            }
          />

          <Route
            path="/user_register_web"
            element={
              <PrivateRoute>
                <UserRegisterWeb />
              </PrivateRoute>
            }
          />

          <Route
            path="*"
            element={
              <Navigate
                to={isAuthenticated ? "/qr-generator" : "/"}
                replace
              />
            }
          />
        </Routes>
      </main>

      <div className="assist">
        <Footer />
      </div>
    </div>
  );
}

export default AppWrapper;
